import {
  Component,
  Input,
  HostListener,
  ViewChild,
  ElementRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { NgStepperModule } from 'angular-ng-stepper';
import { MatDialogModule } from '@angular/material/dialog';
import { A11yModule } from '@angular/cdk/a11y';
import { DialogModule } from '@angular/cdk/dialog';

@Component({
  selector: 'app-btn',
  standalone: true,
  imports: [CommonModule, RouterModule, FontAwesomeModule, CdkStepperModule, NgStepperModule, MatDialogModule, A11yModule, DialogModule],
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.css'],
})
export class ButtonComponent implements AfterViewInit {
  @Input() padding?: string;
  @Input() margin?: string;
  @Input() width: string = 'w-full';
  // Optional dialog-related passthroughs
  @Input() autoFocus?: boolean | '';
  @ViewChild('innerButton') innerButton?: ElementRef<HTMLButtonElement>;

  @Input() disabled = false;
  @Input() loading = false;
  @Input() typeBtn: 'reset' | 'submit' | 'button' = 'button';
  @Input() color:
    | 'success'
    | 'primary'
    | 'danger'
    | 'light'
    | 'cancel'
    | 'cancelBordeadoWhite'
    | 'sky'
    | 'skySoberania'
    | 'white'
    | 'mezclado'
    | 'mezcladoSoberania'
    | 'mezcladoGarantia'
    | 'brand-gradient'
    | 'transparente'
    | 'transparenteCancel'
    | 'bordeado'
    | 'none'
    | 'cancelBordeadoGray' = 'primary';

  @Input() style = {};

  faSpinner = faSpinner;
  isMobile = false;

  constructor() {
    this.updateViewport();
  }

  ngAfterViewInit(): void {
    if (this.autoFocus && this.innerButton?.nativeElement) {
      // Focus after current tick to ensure visibility
      setTimeout(() => this.innerButton?.nativeElement.focus());
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateViewport();
  }

  private updateViewport() {
    this.isMobile = window.innerWidth <= 768;
  }

  get responsivePadding(): string {
    if (this.padding) return this.padding;
    return this.isMobile ? 'py-1 px-3' : 'py-2 px-5';
  }

  get colors() {
    let colors = this.mapColors[this.color];
    if (this.color !== 'none') {
      if (colors) {
        colors = { ...colors, ...this.style };
        if (this.disabled) colors = { ...colors, opacity50: true };
        return colors;
      }
    }
    return this.style;
  }

  get finalClasses(): any {
    return {
      ...this.colors,
      [this.width || '']: true,
      [this.margin || '']: true,
      [this.responsivePadding]: true,
  'font-medium': true,
  'rounded': true,
  'text-sm': true,
  'select-none': true,
  'disabled:opacity-50': this.disabled,
  'disabled:cursor-not-allowed': this.disabled,
    };
  }

  mapColors = {
    success: {
      'bg-brand-success': true,
      'hover:bg-emerald-600': true,
      'focus:ring-brand-success': true,
      'text-white': true,
    },
    primary: {
      'bg-brand-primary': true,
      'hover:bg-brand-primaryDark': true,
      'focus:ring-brand-accent': true,
      'text-white': true,
    },
    danger: {
      'bg-brand-danger': true,
      'hover:bg-red-600': true,
      'focus:ring-brand-danger': true,
      'text-white': true,
    },
    light: {
      'bg-gray-200': true,
      'hover:bg-gray-500': true,
      'focus:ring-gray-50': true,
      'text-gray-700': true,
    },
    cancel: {
      'bg-gray-200': true,
      'hover:bg-gray-300': true,
      'focus:ring-gray-50': true,
      'text-gray-700': true,
    },
    white: {
      'bg-white': true,
      'hover:bg-gray-100': true,
      'ring-gray-50': true,
      'text-gray-700': true,
      border: true,
      'border-[var(--color-black-alt)]': true,
    },
    sky: {
      'bg-brand-accent': true,
      'hover:bg-sky-700': true,
      'focus:ring-brand-accent': true,
      'text-white': true,
    },
    skySoberania: {
      'bg-[var(--color-soberania)]': true,
      'hover:bg-[var(--color-soberania-selected)]': true,
      'focus:bg-[var(--color-soberania-selected)]': true,
      'text-white': true,
    },
    mezclado: {
      'bg-brand-cam': true,
      'text-white': true,
    },
    mezcladoSoberania: {
      'bg-brand-soberania': true,
      'text-white': true,
    },
    mezcladoGarantia: {
      'bg-brand-garantia': true,
      'text-white': true,
    },
    'brand-gradient': {
      'bg-gradient-to-r': true,
      'from-[#057EC9]': true,
      'to-[#02B4C7]': true,
      'text-white': true,
      'hover:from-[#02B4C7]': true,
      'hover:to-[#057EC9]': true,
      'transition-all': true,
      'duration-300': true,
    },
    transparente: {
      'bg-transparent': true,
      'text-brand-primary': true,
      'border-brand-primary': true,
      'border-[0.0125rem]': true,
      'hover:bg-neutral-100': true,
      'focus:ring-brand-accent': true,
    },
    bordeado: {
      'bg-white': true,
      'text-neutral-800': true,
      'border-brand-primary': true,
      'border-[0.0125rem]': true,
      'hover:bg-neutral-100': true,
      'focus:ring-brand-accent': true,
    },
    transparenteCancel: {
      'bg-transparent': true,
      'text-red-500': true,
      'border-red-500': true,
      'border-[0.0125rem]': true,
      'hover:bg-gray-100': true,
      'focus:ring-gray-100': true,
    },
    cancelBordeadoGray: {
      'bg-transparent': true,
      'border-gray-300': true,
      'border-[0.0125rem]': true,
      'hover:bg-gray-100': true,
      'focus:ring-gray-100': true,
    },
    cancelBordeadoWhite: {
      'bg-white': true,
      'border-gray-300': true,
      'border-[0.0125rem]': true,
      'hover:bg-gray-100': true,
      'focus:ring-gray-100': true,
    },
    none: {},
  };
}
